import React, { useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { votersAPI } from "../../../services/api"; 
import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "react-query";

export default function NewResourceModal({ isOpen, onClose, onAddResource }) {
  const [resourceType, setResourceType] = useState("pdf");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [link, setLink] = useState("");
  const queryClient = useQueryClient();

  const createMaterialMutation = useMutation(
    (payload) => votersAPI.createEducationMaterial(payload),
    {
      onSuccess: (res) => {
        const created = res?.data?.data?.material;
        if (created) {
          onAddResource({
            title: created.title,
            description: created.description,
            media: {
              type: created.file_url?.endsWith(".pdf") ? "pdf" : "link",
              link: created.file_url,
            },
          });
        }
        // Invalidate any lists if present
        queryClient.invalidateQueries("education-materials");
        toast.success("Material created successfully!");
        // Reset form
        setTitle("");
        setDescription("");
        setFile(null);
        setLink("");
        setResourceType("pdf");
        onClose();
      },
      onError: (err) => {
        const msg = err?.response?.data?.message || "Failed to create material. Please try again.";
        toast.error(msg);
      },
    }
  );

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleSubmit = async () => {
    // Basic validations with toasts
    if (!title.trim()) {
      toast.error("Please enter a title.");
      return;
    }

    if (resourceType === "pdf") {
      if (!file) {
        toast.error("Please upload a PDF file.");
        return;
      }
      const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
      if (!isPdf) {
        toast.error("File must be a PDF.");
        return;
      }
      const maxBytes = 10 * 1024 * 1024; // 10MB
      if (file.size > maxBytes) {
        toast.error("PDF must be 10MB or less.");
        return;
      }
    }

    if (resourceType === "link") {
      if (!link.trim()) {
        toast.error("Please enter a link URL.");
        return;
      }
      try {
        // Validate URL shape
        const u = new URL(link.trim());
        if (!/^https?:$/.test(u.protocol)) throw new Error("Invalid protocol");
      } catch {
        toast.error("Please enter a valid http(s) URL.");
        return;
      }
    }

    let payload;

    if (resourceType === "pdf") {
      //  Send multipart form data for PDF uploads
      payload = new FormData();
      payload.append("title", title);
      payload.append("description", description);
      payload.append("file", file);
    } else {
      //  Send regular JSON for link-type resources
      payload = {
        title,
        description,
        file_url: link,
      };
    }

    await createMaterialMutation.mutateAsync(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white w-full max-w-3xl rounded-xl shadow-lg p-6 relative animate-in slide-in-from-top duration-500 max-h-[90vh] flex flex-col">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-transform transform hover:rotate-90 duration-200"
        >
          <XMarkIcon className="w-6 h-6 text-gray-500" />
        </button>

        {/* Header */}
        <h2 className="text-2xl font-bold mb-4">New Resource</h2>
        <hr className="-mx-6 mt-4 mb-4 border-t border-gray-300" />

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto pr-2 flex flex-col gap-4">
          {/* Type selection */}
          <div className="flex gap-4 mt-2">
            {["pdf", "link"].map((type) => (
              <label
                key={type}
                className={`flex items-center gap-2 w-full p-3 border rounded-lg cursor-pointer transition ${
                  resourceType === type
                    ? "border-blue-600 bg-white"
                    : "border-gray-300 bg-white"
                }`}
              >
                <input
                  type="radio"
                  name="resourceType"
                  value={type}
                  className="form-radio text-blue-600"
                  checked={resourceType === type}
                  onChange={() => setResourceType(type)}
                />
                {type === "link" ? "Link" : type.toUpperCase()}
              </label>
            ))}
          </div>

          {/* Title */}
          <div>
            <label className="font-semibold">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter title"
              className="mt-1 w-full border border-gray-300 rounded-md p-2"
            />
          </div>

          {/* Description */}
          <div>
            <label className="font-semibold">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description"
              className="mt-1 w-full border border-gray-300 rounded-md p-2"
              rows={4}
            />
          </div>

          {/* PDF Upload */}
          {resourceType === "pdf" && (
            <div>
              <label className="font-semibold mb-2 block">Attachment</label>
              <label className="mb-2 block text-gray-600 font-semibold text-sm">
                Upload PDF File
              </label>
              <div className="relative mt-1 w-full border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center text-center cursor-pointer hover:border-gray-400">
                {file ? (
                  <>
                    <svg
                      className="w-10 h-10 text-green-500 mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5-5 5 5M12 5v12"
                      />
                    </svg>
                    <p className="text-sm text-green-600 font-medium">
                      Selected: {file.name}
                    </p>
                  </>
                ) : (
                  <>
                    <svg
                      className="w-10 h-10 text-gray-400 mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5-5 5 5M12 5v12"
                      />
                    </svg>
                    <span className="text-blue-600 font-medium">Upload file</span>
                    <span className="text-gray-700 ml-1">or drag and drop</span>
                    <p className="text-gray-500 text-sm mt-1">
                      PDF only up to 10MB
                    </p>
                  </>
                )}
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handleFileChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
              </div>
            </div>
          )}

          {/* Link Input */}
          {resourceType === "link" && (
            <div>
              <label className="font-semibold mb-1 block">URL</label>
              <input
                type="text"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="https://www.sitename.com"
                className="mt-1 w-full border border-gray-300 rounded-md p-2 focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
              />
            </div>
          )}
        </div>

        {/* Divider */}
        <hr className="-mx-6 mt-4 mb-4 border-t border-gray-300" />

        {/* Buttons */}
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={createMaterialMutation.isLoading}
            className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={createMaterialMutation.isLoading}
            className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {createMaterialMutation.isLoading ? "Saving..." : "Add Resource"}
          </button>
        </div>
      </div>
    </div>
  );
}
